/* eslint-disable max-lines-per-function */
import { QueryResult, useMutation } from "@apollo/client";
import { Link, navigate } from "@reach/router";
import React from "react";
import { Viewer } from "components/auth/types";
import { SubmitValues, TemplateForm } from "components/send-messages";
import { TemplateSelector } from "components/send-messages/template-selector";
import {
  ProcessRemovals,
  ProcessRemovalsVariables,
  getTemplateGroup_templateGroup as GroupTemplate,
  ProcessRemovalsView,
  ProcessRemovalsViewVariables,
} from "types/gql-types";
import logger from "services/logger";
import { calculateMessageValues } from "utils/template";
import { useSnackbar } from "components/haven-design/snackbar";
import { BackButton } from "components/haven-design";
import { PROCESS_REMOVALS_SUBMIT } from "../query";
import { Template } from "../types";

interface Props {
  results: QueryResult<ProcessRemovalsView, ProcessRemovalsViewVariables>
  emailTemplate: Template | null;
  hasSkippedLetters: boolean;
  letterTemplate: Template | null;
  path: string;
  smsTemplate: Template | null;
  stepComplete: boolean;
  viewer: Viewer;
}

const GoToRemovals = () => (
  <div style={{ marginTop: -47, float: "left" }}>
    <BackButton color="secondary" size="L" onClick={() => navigate("removals")} />
  </div>);

const StepHeader: React.FC<{count: number}> = ({ count }) => (
  <>
    <h2 style={{ marginBottom: 18 }}>Choose Template and Send</h2>
    <p className="color-secondary margin-bottom-m">
    Select a template to send to{" "}
      <b>{count} applicants</b> about their removal.
    </p>
    <p className="color-secondary margin-bottom-m">
      You can only use existing templates. If you need to make any edits or a add a new template,
      visit <Link to="/dash/manager/settings/templates">Template Settings</Link>.
    </p>
  </>);

const PreviewDenialTemplates: React.FC<Props> = (props) => {
  const { results: { data } } = props;
  const removals = data?.interestList.scheduledUpdate.removals;
  const [groupTemplate, setGroupTemplate] = React.useState<GroupTemplate | null>(null);
  const updateValues = calculateMessageValues(groupTemplate);
  const [submit, result] = useMutation<ProcessRemovals, ProcessRemovalsVariables>(
    PROCESS_REMOVALS_SUBMIT,
  );
  const { openSnackbar } = useSnackbar();
  const handleSubmit = ({ emailEnabled, smsEnabled, letterEnabled }: SubmitValues) => {
    if (groupTemplate === null) return;
    submit({
      variables: {
        interestList: data?.interestList.id ?? "",
        templateGroupId: groupTemplate.id,
        emailEnabled,
        smsEnabled,
        letterEnabled,
      },
    })
      .then(() => {
        navigate("success", { state: { enqueued: (removals?.totalCount || 0) > 500 } });
        props.results.refetch();
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Processing applicants failed", status: "error" });
      });
  };
  return (
    <div>
      <StepHeader count={removals?.totalCount ?? 0}/>
      <TemplateSelector
        onChange={setGroupTemplate}
        viewer={props.viewer}
        currentTemplate={groupTemplate}
      />
      <div style={{ height: 20 }} />
      <TemplateForm
        viewer={props.viewer}
        isSending={result.loading}
        handleSubmit={handleSubmit}
        template={groupTemplate}
        readOnly
        currentListId={data?.interestList.id ?? ""}
        allowCopy={false}
        {...updateValues}
      />
      <GoToRemovals />
    </div>
  );
};

export default PreviewDenialTemplates;
